import { ChangeDetectionStrategy, Component, ElementRef, Inject, Optional, ViewChild } from '@angular/core';
import { BundleOptionSelection } from '@df/catalog/product/bundle-option-selection';
import { BundleSelection } from '@df/catalog/product/bundle-selection';
import { IProductDataBundleOptionChild, Product } from '@df/catalog/product/product';
import { ISizeName } from '@df/core/service/client';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { IfNotTradeStoreDirective } from 'projects/app-default/src/app/multisite/trade/if-not-trade-store.directive';
import { EGtmAddToCartVia } from '../../../../../module/gtm/gtm.service.decorator';
import { ModalLayoutRightComponent, NModalLayoutRight } from '../../../../../ui/modal/modal-layout-right.component';
import { ProductBundleOptionsAddToBasketComponent } from '../add-to-basket/product-bundle-options-add-to-basket.component';
import { ProductBundleOptionsListComponent } from '../list/product-bundle-options-list.component';
import { NProductBundleOptions } from './product-bundle-options-modal.interface';
@Component({
    selector: 'product-bundle-options-modal',
    templateUrl: './product-bundle-options-modal.component.html',
    standalone: true,
    imports: [
        ICmsClientModule,
        IfNotTradeStoreDirective,
        ModalLayoutRightComponent,
        PriceComponent,
        ProductBundleOptionsAddToBasketComponent,
        ProductBundleOptionsListComponent,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@DfCmsModal({
    id: NProductBundleOptions.MODAL_ID,
    name: 'Product Bundle Options',
    position: (sizeId: ISizeName) => (sizeId === 'S' ? 'bottom' : 'right'),
    config: (sizeId: ISizeName) => ({
        ...NModalLayoutRight.ModalConfig,
        minWidth: sizeId === 'S' ? '100%' : '492px',
        width: { S: '100%', M: '50%', L: '35%', X: '35%' }[sizeId],
        modalClass: 'bg-col-w bg-col-t-s p-a-0 overflow-visible',
        scrollStrategy: 'block',
        autoFocus: true,
        trapFocus: true
    }),
    reopenPolicy: NDfModal.ReopenPolicy.wait,
    sizeRestriction: sizeId => ({ S: ['S'], M: ['M'], L: ['L', 'X'], X: ['L', 'X'] })[sizeId]
})
@ICmsComponent<ProductBundleOptionsModalComponent>({
    id: NProductBundleOptions.MODAL_ID,
    block: {
        url: NProductBundleOptions.MODAL_ID
    },
    onDemandOnly: true,
    name: 'Product Bundle Options',
    fields: [
        {
            key: 'description',
            type: NICmsContentField.EFieldType.text,
            title: 'Description',
            default: 'Sed a volutpat urna, neque purus sed facilisis. Pretium suspendisse sit risus lorem.'
        }
    ]
})
export class ProductBundleOptionsModalComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content: NProductBundleOptions.IBlockData;

    protected EGtmAddToCartVia = EGtmAddToCartVia;

    product?: Product;

    bundleSelection?: BundleSelection;

    @ViewChild('contentContainer', { read: ElementRef })
    contentContainer: ElementRef;

    constructor(
        @Optional() protected modalRef: ModalRef<ProductBundleOptionsModalComponent>,
        @Optional() @Inject(MODAL_DATA) public data: NProductBundleOptions.IModalData
    ) {
        super();

        this.product = data.product;
        this.bundleSelection = data.selection;
    }
    /**
     * Filters out of stock children, except the ones currently selected, used to get children that will display in bundle selection.
     */
    filterChildren(children: IProductDataBundleOptionChild[], option: BundleOptionSelection): IProductDataBundleOptionChild[] {
        return children.filter(
            child => child.product.isInStock() || Object.keys(option.selection).indexOf(child.selection_id.toString()) !== -1
        );
    }
    get qty() {
        return this.data.qty || 1;
    }
    get buyRequest() {
        return {
            ...this.data.buyRequest,
            bundle_options: this.bundleSelection?.selection
        };
    }
    get price() {
        return this.product?.getPrice({ bundle_options: this.bundleSelection?.selection , qty : 1});
    }
    get orgPrice() {
        return this.product?.getOrgPrice({ bundle_options: this.bundleSelection?.selection , qty : 1});
    }
    get hasEditableOptions(): boolean {
        return !!this.bundleSelection?.editableOptions?.length;
    }

    protected close(): void {
        this.modalRef.close();
    }
}
